import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log to error tracking service (future)
    // logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      let showDebug = false;
      try {
        const qs = new URLSearchParams(window.location.search);
        showDebug =
          process.env.NODE_ENV === 'development' ||
          qs.get('debugErrors') === '1' ||
          qs.get('debugErrors') === 'true' ||
          localStorage.getItem('debugErrors') === '1';
      } catch {
        showDebug = process.env.NODE_ENV === 'development';
      }

      const globalErr = (() => {
        try {
          return window.__SP_LAST_ERROR__ || null;
        } catch {
          return null;
        }
      })();

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-red-900">Si è verificato un errore</CardTitle>
                  <p className="text-sm text-gray-600 mt-1">Ci scusiamo per l'inconveniente</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-900 font-medium mb-2">Dettagli errore:</p>
                <p className="text-sm text-red-800 font-mono">
                  {this.state.error && this.state.error.toString()}
                </p>
              </div>

              {showDebug && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-900 font-medium mb-2">Dettagli tecnici (debug):</p>

                  {globalErr ? (
                    <div className="text-xs text-gray-800 font-mono space-y-2">
                      <div>
                        <div>type: {String(globalErr.type || '')}</div>
                        <div>file: {String(globalErr.filename || '')}</div>
                        <div>
                          line: {String(globalErr.lineno || '')} col: {String(globalErr.colno || '')}
                        </div>
                      </div>
                      {globalErr.stack ? (
                        <pre className="text-xs bg-white p-3 rounded overflow-auto max-h-64 whitespace-pre-wrap">{String(globalErr.stack)}</pre>
                      ) : null}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-700">Nessun dettaglio globale disponibile.</p>
                  )}

                  {this.state.error?.stack ? (
                    <details className="mt-3">
                      <summary className="text-sm font-medium text-gray-700 cursor-pointer">Stack trace (ErrorBoundary)</summary>
                      <pre className="text-xs bg-white p-3 rounded mt-2 overflow-auto max-h-64 whitespace-pre-wrap">{String(this.state.error.stack)}</pre>
                    </details>
                  ) : null}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>Cosa puoi fare:</strong>
                </p>
                <ul className="text-sm text-blue-800 mt-2 space-y-1">
                  <li>• Ricarica la pagina</li>
                  <li>• Torna alla home</li>
                  <li>• Contatta il supporto se il problema persiste</li>
                </ul>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={this.handleReload}
                  className="flex-1 bg-blue-900 hover:bg-blue-800"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Ricarica Pagina
                </Button>
                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="flex-1"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Torna alla Home
                </Button>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                <details className="mt-4">
                  <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                    Stack trace (dev only)
                  </summary>
                  <pre className="text-xs bg-gray-100 p-4 rounded mt-2 overflow-auto max-h-64">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}

              <div className="text-center text-sm text-gray-600 pt-4 border-t">
                <p>Se il problema persiste, contatta:</p>
                <p className="font-medium text-blue-900 mt-1">support@sostegnopro.it</p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;